import moment, { type Moment } from 'moment-timezone';
import { type SchedulerDetailsTypes } from '@/components/shared/interview-scheduler/SchedulerDetails/SchedulerDetails.types';
import { v4 as uuidv4 } from 'uuid';

// Type definitions for events, availability slots, and the calendar API
interface Event {
  id: string;
  start: string; // ISO 8601 string
  end: string; // ISO 8601 string
  interviewerId: string | number;
  title?: string;
  subject?: string;
}

interface AvailabilitySlot {
  start: string; // ISO 8601 string
  end: string; // ISO 8601 string
}

interface CommonAvailability {
  [weekRange: string]: AvailabilitySlot[];
}

// Helper function to round a moment to the nearest next 15 minutes
// This ensures that time slots align with a 15-minute grid for consistency
const roundToNearest15Minutes = (momentTime: Moment): Moment => {
  const minutes = momentTime.minutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  return momentTime.clone().minutes(roundedMinutes).seconds(0).milliseconds(0);
};

// Helper function to create an available slot object
// This standardizes the format of available slots for the calendar UI
const createAvailableSlot = (
  dateKey: string,
  start: Moment,
  end: Moment,
  timezone: string,
  crossDayBoundary = false
): SchedulerDetailsTypes.AvailableSlot => ({
  id: `${dateKey}-${start.format('HH:mm')}-${end.format('HH:mm')}`,
  start: start.toISOString(true),
  end: end.toISOString(true),
  category: 'available',
  title: 'Available',
  backgroundColor: 'transparent',
  borderColor: 'var(--kula-colors-primary-100)',
  textColor: 'transparent',
  editable: false,
  classNames: ['available-event-wrapper-animation'],
  extendedProps: {
    id: uuidv4(),
    category: 'available',
    description: 'Available for booking',
    type: 'manual-available',
    crossDayBoundary: crossDayBoundary,
    originalDayKey: dateKey,
  },
});

// Main function to calculate available slots based on common availability and busy events
export const getManualAvailableSlots = (
  commonAvailability: CommonAvailability, // Availability windows for the week
  busyEvents: Event[], // List of busy events from the calendar
  timezone: string, // FullCalendar API for timezone and other settings
  interviewerIds: (string | number)[], // List of interviewer IDs to filter events
  slotDuration: number = 15, // Minimum duration for a valid slot in minutes
  dateFormat: { label: string }
): SchedulerDetailsTypes.AvailableSlot[] => {
  const availableSlots: SchedulerDetailsTypes.AvailableSlot[] = [];

  const now: Moment = moment().tz(timezone); // Current time in the specified timezone
  const slotDurationMs: number = slotDuration * 60 * 1000; // Convert slot duration to milliseconds

  // Step 1: Group busy events by date and interviewer for efficient lookup
  // This pre-processing step reduces the need to iterate over all events multiple times
  const eventsByDateAndInterviewer: Record<
    string,
    Record<string | number, Event[]>
  > = {};

  busyEvents.forEach((event) => {
    const eventStart: Moment = moment(event.start).tz(timezone);
    const eventEnd: Moment = moment(event.end).tz(timezone);
    const interviewerId: string | number = event.interviewerId;

    // Iterate through each date the event spans to handle multi-day events
    const currentDate: Moment = eventStart.clone().startOf('day');
    const endDate: Moment = eventEnd.clone().startOf('day');

    while (currentDate.isSameOrBefore(endDate, 'day')) {
      const dateKey: string = currentDate.format(dateFormat?.label);
      if (!eventsByDateAndInterviewer[dateKey]) {
        eventsByDateAndInterviewer[dateKey] = {};
      }
      if (!eventsByDateAndInterviewer[dateKey][interviewerId]) {
        eventsByDateAndInterviewer[dateKey][interviewerId] = [];
      }

      // Adjust the event's start and end to fit within the current date
      const dayStart: Moment = currentDate.clone().startOf('day');
      const dayEnd: Moment = currentDate.clone().endOf('day');
      const adjustedStart: Moment = moment.max(eventStart, dayStart);
      const adjustedEnd: Moment = moment.min(eventEnd, dayEnd);

      // Only include events that have a duration on this date
      if (
        adjustedStart.isBefore(adjustedEnd) ||
        adjustedStart.isSame(adjustedEnd)
      ) {
        eventsByDateAndInterviewer[dateKey][interviewerId].push({
          ...event,
          start: adjustedStart.toISOString(),
          end: adjustedEnd.toISOString(),
        });
      }

      currentDate.add(1, 'day');
    }
  });

  // Step 2: Process each availability slot to find free periods
  Object.entries(commonAvailability).forEach(
    ([_weekRange, availabilitySlots]: [string, AvailabilitySlot[]]) => {
      availabilitySlots.forEach((slot: AvailabilitySlot) => {
        // Convert slot times from UTC to the target timezone
        const slotStartMoment: Moment = moment.utc(slot.start).tz(timezone);
        const slotEndMoment: Moment = moment.utc(slot.end).tz(timezone);

        // Skip slots that are entirely in the past
        if (slotEndMoment.isBefore(now)) {
          return;
        }

        // Check if this slot crosses day boundaries in the current timezone
        const crossesDayBoundary = !slotStartMoment.isSame(
          slotEndMoment,
          'day'
        );

        // If slot crosses day boundary, we need to handle each day separately
        if (crossesDayBoundary) {
          const currentDate = slotStartMoment.clone().startOf('day');
          const endDate = slotEndMoment.clone().startOf('day');

          while (currentDate.isSameOrBefore(endDate, 'day')) {
            const dateKey = currentDate.format(dateFormat?.label);
            const dayStart = currentDate.clone().startOf('day');
            const dayEnd = currentDate.clone().endOf('day');

            // Calculate the portion of the slot that falls on this day
            const daySlotStart = moment.max(slotStartMoment, dayStart);
            const daySlotEnd = moment.min(slotEndMoment, dayEnd);

            // Only process if there's a valid time range on this day
            if (daySlotEnd.isAfter(daySlotStart)) {
              processDaySlot(
                dateKey,
                daySlotStart,
                daySlotEnd,
                eventsByDateAndInterviewer,
                now,
                slotDurationMs,
                crossesDayBoundary,
                availableSlots,
                timezone
              );
            }

            currentDate.add(1, 'day');
          }
        } else {
          // For single-day slots, process normally
          const dateKey = slotStartMoment.format(dateFormat?.label);
          processDaySlot(
            dateKey,
            slotStartMoment,
            slotEndMoment,
            eventsByDateAndInterviewer,
            now,
            slotDurationMs,
            false,
            availableSlots,
            timezone
          );
        }
      });
    }
  );

  return availableSlots;
};

// Helper function to process a single day's portion of an availability slot
const processDaySlot = (
  dateKey: string,
  slotStartMoment: Moment,
  slotEndMoment: Moment,
  eventsByDateAndInterviewer: Record<string, Record<string | number, Event[]>>,
  now: Moment,
  slotDurationMs: number,
  crossesDayBoundary: boolean,
  availableSlots: SchedulerDetailsTypes.AvailableSlot[],
  timezone: string
) => {
  // Initialize timePointer to the slot start, adjusted for current time
  let timePointer: Moment = slotStartMoment.clone().startOf('minute');
  if (slotStartMoment.isSame(now, 'day')) {
    const roundedNow = roundToNearest15Minutes(now.clone());
    if (roundedNow.isAfter(slotStartMoment)) {
      timePointer = roundedNow;
    }
  }

  // Skip if the adjusted start time is already at or after the end time
  if (timePointer.isSameOrAfter(slotEndMoment)) {
    return;
  }

  // Check if the slot's start day is fully busy
  let isStartDayFullyBusy = false;
  const startDayEvents = eventsByDateAndInterviewer[dateKey] || {};
  const dayStart: Moment = slotStartMoment.clone().startOf('day');
  const slotStartOnDay: Moment = moment.max(slotStartMoment, dayStart);

  // Check all events on the start date to see if the slot's portion is fully covered
  Object.values(startDayEvents).forEach((events: Event[]) => {
    events.forEach((event: Event) => {
      const eventStart: Moment = moment(event.start).tz(timezone);
      const eventEnd: Moment = moment(event.end).tz(timezone);

      if (
        eventStart.isBefore(slotEndMoment) &&
        eventEnd.isAfter(slotStartMoment) &&
        eventStart.isSameOrBefore(slotStartMoment) &&
        eventEnd.isSameOrAfter(slotStartOnDay.clone().endOf('day'))
      ) {
        isStartDayFullyBusy = true;
      }
    });
  });

  // If the start day is fully busy, skip this slot
  if (isStartDayFullyBusy) {
    return;
  }

  // Collect all busy periods that overlap with the slot for this day
  const busyPeriods: { start: Moment; end: Moment }[] = [];
  const events = eventsByDateAndInterviewer[dateKey] || {};

  Object.values(events).forEach((eventList: Event[]) => {
    eventList.forEach((event: Event) => {
      const eventStart: Moment = moment(event.start).tz(timezone);
      const eventEnd: Moment = moment(event.end).tz(timezone);

      // Check if the event overlaps with the slot
      if (
        eventStart.isBefore(slotEndMoment) &&
        eventEnd.isAfter(slotStartMoment)
      ) {
        busyPeriods.push({
          start: moment.max(eventStart, slotStartMoment),
          end: moment.min(eventEnd, slotEndMoment),
        });
      }
    });
  });

  // Sort and merge overlapping busy periods
  busyPeriods.sort((a, b) => a.start.valueOf() - b.start.valueOf());
  const mergedBusyPeriods: { start: Moment; end: Moment }[] = [];

  busyPeriods.forEach((period) => {
    if (mergedBusyPeriods.length === 0) {
      mergedBusyPeriods.push(period);
      return;
    }
    const lastPeriod = mergedBusyPeriods[mergedBusyPeriods.length - 1];
    if (period.start.isSameOrBefore(lastPeriod.end)) {
      lastPeriod.end = moment.max(lastPeriod.end, period.end);
    } else {
      mergedBusyPeriods.push(period);
    }
  });

  // Create free slots between busy periods
  let lastCheckedTime: Moment = timePointer.clone();

  if (mergedBusyPeriods.length === 0) {
    // No busy periods; the entire slot (from timePointer to end) is available
    const adjustedStart = moment.max(lastCheckedTime, timePointer);
    if (
      slotEndMoment.isAfter(adjustedStart) &&
      slotEndMoment.diff(adjustedStart) >= slotDurationMs
    ) {
      availableSlots.push(
        createAvailableSlot(
          dateKey,
          adjustedStart,
          slotEndMoment,
          timezone,
          crossesDayBoundary
        )
      );
    }
  } else {
    // Iterate through merged busy periods to find gaps
    mergedBusyPeriods.forEach((period, _index) => {
      const gapStart: Moment = lastCheckedTime;
      const gapEnd: Moment = period.start;

      // Adjust the gap start to be at least timePointer to avoid past slots
      const adjustedGapStart = moment.max(gapStart, timePointer);

      // Only create a slot if the adjusted gap is in the future and long enough
      if (
        gapEnd.isAfter(adjustedGapStart) &&
        gapEnd.diff(adjustedGapStart) >= slotDurationMs
      ) {
        availableSlots.push(
          createAvailableSlot(
            dateKey,
            adjustedGapStart,
            gapEnd,
            timezone,
            crossesDayBoundary
          )
        );
      }

      lastCheckedTime = period.end.clone();
    });

    // Check for a final gap after the last busy period
    const adjustedFinalStart = moment.max(lastCheckedTime, timePointer);
    if (
      slotEndMoment.isAfter(adjustedFinalStart) &&
      slotEndMoment.diff(adjustedFinalStart) >= slotDurationMs
    ) {
      availableSlots.push(
        createAvailableSlot(
          dateKey,
          adjustedFinalStart,
          slotEndMoment,
          timezone,
          crossesDayBoundary
        )
      );
    }
  }
};
